import { z } from 'zod'

export const WORK_WRITE_CONTRACT_VERSION = '1.0.0' as const

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Work write query values remain shape-checked without taking ownership of org validation order. */
export const workWriteQuerySchema = z.object({
  orgId: optionalQueryString,
}).passthrough()

const nullableOptionalString32 = z.string().max(32).nullable().optional()
const nullableOptionalString1k = z.string().max(1_000).nullable().optional()
const nullableOptionalString5k = z.string().max(5_000).nullable().optional()
const nullableOptionalString10k = z.string().max(10_000).nullable().optional()
const nullableOptionalPositiveInteger = z.number().positive().refine(Number.isSafeInteger).nullable().optional()

/**
 * These request contracts describe the current Work wire behavior. The Work
 * handlers retain their hand-authored validation and defaults; unknown fields
 * stay passthrough for legacy clients, and null optional values are accepted.
 * Canonical task creates accept an optional Idempotency-Key header.
 * This covers canonical WVS task creates and TT project creates on every
 * mount: a fresh keyed create returns 201, and a same-org replay returns 200
 * with `replayed: true` and the original row. Creates without a key, and the
 * WVS facade/legacy task aliases (quick-add, confirm-task-request), stay
 * non-idempotent.
 */
export const workSubmissionCreateRequestSchema = z.object({
  period: z.enum(['daily', 'weekly']).nullable().optional(),
  status: z.enum(['draft', 'submitted']).nullable().optional(),
  submission_date: nullableOptionalString32,
  summary: nullableOptionalString5k,
  blockers: nullableOptionalString5k,
  next_steps: nullableOptionalString5k,
}).passthrough()

export const workSubmissionApproveRequestSchema = z.object({
  decision: z.enum(['approved', 'rejected']).nullable().optional(),
  reason: nullableOptionalString1k,
}).passthrough()

export const workTaskCreateRequestSchema = z.object({
  title: z.string().max(240).refine((value) => value.trim().length > 0),
  description: nullableOptionalString10k,
  status: z.enum(['todo', 'in_progress', 'blocked', 'done', 'cancelled']).nullable().optional(),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).nullable().optional(),
  project_id: nullableOptionalPositiveInteger,
  assignee_user_id: nullableOptionalPositiveInteger,
  source_submission_id: nullableOptionalPositiveInteger,
  due_date: nullableOptionalString32,
}).passthrough()

const submissionSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  userId: z.string(),
  period: z.enum(['daily', 'weekly']),
  submissionDate: z.string(),
  summary: z.string().nullable(),
  blockers: z.string().nullable(),
  nextSteps: z.string().nullable(),
  status: z.enum(['draft', 'submitted', 'approved', 'rejected']),
  approvedByUserId: z.string().nullable(),
  approvedAt: z.string().nullable(),
  rejectionReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

const taskSummarySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  createdByUserId: z.string(),
  assigneeUserId: z.string().nullable(),
  projectId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: z.enum(['todo', 'in_progress', 'blocked', 'done', 'cancelled']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  sourceSubmissionId: z.string().nullable(),
  dueDate: z.string().nullable(),
  completedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
}).passthrough()

const writeSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

const submissionWriteResponseShape = {
  contract_version: z.literal(WORK_WRITE_CONTRACT_VERSION),
  submission: submissionSummarySchema,
  sideEffects: writeSideEffectsSchema,
}

const taskWriteResponseShape = {
  contract_version: z.literal(WORK_WRITE_CONTRACT_VERSION),
  task: taskSummarySchema,
  replayed: z.boolean(),
  sideEffects: writeSideEffectsSchema,
}

export const workSubmissionCreateResponseSchema = z.object(submissionWriteResponseShape).passthrough()
export const workSubmissionApproveResponseSchema = z.object(submissionWriteResponseShape).passthrough()
export const workTaskCreateResponseSchema = z.object(taskWriteResponseShape).passthrough()

export type WorkSubmissionCreateRequest = z.infer<typeof workSubmissionCreateRequestSchema>
export type WorkSubmissionApproveRequest = z.infer<typeof workSubmissionApproveRequestSchema>
export type WorkTaskCreateRequest = z.infer<typeof workTaskCreateRequestSchema>
export type WorkSubmissionCreateResponse = z.infer<typeof workSubmissionCreateResponseSchema>
export type WorkSubmissionApproveResponse = z.infer<typeof workSubmissionApproveResponseSchema>
export type WorkTaskCreateResponse = z.infer<typeof workTaskCreateResponseSchema>
